<script setup lang="ts">
import CheckoutTimeline from "@/shop/components/checkout/CheckoutTimeline.vue"
import { useCartStore } from "@/shop/store"
import { useRouter } from "vue-router"
import { useCustomDeliveryAddressStore } from "@/shop/store/useCustomDeliveryAddressStore"
import { storeToRefs } from "pinia"
import AddressHistoryPicker from "@/common/components/AddressHistoryPicker.vue"
import type { AddressHistory } from "@/model/backend/address-history.ts"
import { useShopDialogs } from "@/shop/composables/useShopDialogs.ts"
import { useHttpClient } from "@/vf"
import { ref } from "vue"

const router = useRouter()
const cartStore = useCartStore()
const dialogs = useShopDialogs()
const http = useHttpClient()
const customDeliveryAddressStore = useCustomDeliveryAddressStore()
const { newShippingAddress } = storeToRefs(customDeliveryAddressStore)
const AddressPicker = ref<InstanceType<typeof AddressHistoryPicker> | null>(null)

const allowedZipsForCarrier = [
    21109, 21107, 20539, 20457, 21079, 21073, 22113, 21033, 22117, 22111, 22119, 20359, 20537, 20459, 22043, 22041,
    20097, 20099, 20095, 20354, 20355, 20357, 22767, 22769, 22765, 22763, 22605, 22761, 22525, 20535, 22087, 22089,
    22081, 22083, 22085,
].sort()

function onSelectAddress(address: Partial<AddressHistory>) {
    cartStore.shippingAddress.firstName = address.firstName?.trim() || ""
    cartStore.shippingAddress.lastName = address.lastName?.trim() || ""
    cartStore.shippingAddress.street = address.street?.trim() || ""
    cartStore.shippingAddress.zip = address.zip?.trim() || ""
    cartStore.shippingAddress.city = address.city?.trim() || ""
    cartStore.shippingAddress.additionalAddress = address.additionalAddress?.trim() || ""
    cartStore.shippingAddress.deliveryNote = address.deliveryNote?.trim() || ""

    router.push({ name: "prescription" })
}

async function deleteAddress(address: AddressHistory) {
    if (!address.id) {
        return
    }

    if (!(await dialogs.confirm("Adressen löschen?", "Willst du die Adresse wirklich löschen?"))) {
        return
    }

    try {
        await http.delete(`/address-history/${address.id}`)
    } catch (_) {
        await dialogs.alert(
            "Löschen fehlgeschlagen",
            "Die Adresse konnte nicht gelöscht werden. Bitte später erneut versuchen.",
        )
        return
    }
    await AddressPicker.value?.refresh()
}
</script>

<template>
    <div class="container my-5">
        <div class="small text-muted mb-5 pb-3">Home / Warenkorb</div>
        <h2>Übersicht</h2>
        <div class="mb-3">
            <h1>Lieferadresse</h1>
        </div>
        <CheckoutTimeline :step="4" />
        <h3 class="mt-4">Lieferadresse wählen</h3>
        <AddressHistoryPicker ref="AddressPicker" class="mt-4" @select="onSelectAddress" @delete="deleteAddress" />
        <div class="d-flex align-items-center mt-4">
            <hr class="flex-grow-1" />
            <span class="mx-2 my-0 h3">Oder</span>
            <hr class="flex-grow-1" />
        </div>
        <h3 class="mt-4">Neue Lieferadresse eingeben</h3>
        <div class="row mt-4">
            <div class="col-md-6">
                <div class="form-group">
                    <label for="firstName">Vorname</label>
                    <input
                        id="firstName"
                        v-model="newShippingAddress.firstName"
                        type="text"
                        class="form-control"
                        placeholder="Vorname"
                        required
                    />
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label for="lastName">Nachname</label>
                    <input
                        id="lastName"
                        v-model="newShippingAddress.lastName"
                        type="text"
                        class="form-control"
                        placeholder="Nachname"
                        required
                    />
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label for="street">Straße</label>
                    <input
                        id="street"
                        v-model="newShippingAddress.street"
                        type="text"
                        class="form-control"
                        placeholder="Straße"
                        required
                    />
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label for="additionalAddress">Adresszusatz</label>
                    <input
                        id="additionalAddress"
                        v-model="newShippingAddress.additionalAddress"
                        type="text"
                        class="form-control"
                        placeholder="Hinterhof, Etage, Apartment, ..."
                        required
                        autocomplete="off"
                    />
                </div>
            </div>
            <div class="col-md-6">
                <div class="row">
                    <div class="col-md-5">
                        <div class="form-group">
                            <label for="zip">PLZ</label>
                            <input
                                v-if="cartStore.shippingMethod !== 'carrier'"
                                id="zip"
                                v-model="newShippingAddress.zip"
                                type="text"
                                class="form-control"
                                placeholder="PLZ"
                                required
                            />
                            <select
                                v-if="cartStore.shippingMethod === 'carrier'"
                                id="zip"
                                v-model="newShippingAddress.zip"
                                class="form-control custom-select"
                            >
                                <option value></option>
                                <option v-for="zip in allowedZipsForCarrier" :key="zip" :value="zip">
                                    {{ zip }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-7">
                        <div class="form-group">
                            <label for="city">Stadt</label>
                            <input
                                id="city"
                                v-model="newShippingAddress.city"
                                type="text"
                                class="form-control"
                                placeholder="Stadt"
                                required
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label for="deliveryNote">Lieferhinweis</label>
                    <input
                        id="deliveryNote"
                        v-model="newShippingAddress.deliveryNote"
                        type="text"
                        class="form-control"
                        placeholder="Lieferhinweis"
                        required
                    />
                </div>
            </div>
        </div>

        <div class="text-right mt-4">
            <button
                v-if="customDeliveryAddressStore.addressComplete"
                class="btn btn-primary"
                @click="onSelectAddress(newShippingAddress)"
            >
                Weiter
            </button>
            <button v-else class="btn btn-primary" type="button" disabled>Weiter</button>
        </div>
    </div>
</template>
